import { useCallback, useMemo, useRef, useState } from 'react';
import { isMobileApp } from '@/lib/platform';

// Lazy import to avoid issues on web
let NativeBiometric: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  NativeBiometric = (globalThis as any).require?.('capacitor-native-biometric')?.NativeBiometric ?? null;
} catch {
  NativeBiometric = null;
}
// Fallback: dynamic import for ESM environments
if (!NativeBiometric) {
  import('capacitor-native-biometric').then(mod => { NativeBiometric = mod.NativeBiometric; }).catch(() => {});
}

type Options = {
  enabledKey?: string;
  cacheKey?: string;
  ttlMs?: number;
};

export function useBiometricGate(opts: Options = {}) {
  const enabledKey = opts.enabledKey ?? 'giftmap_bio_enabled';
  const cacheKey = opts.cacheKey ?? 'giftmap_bio_last_ok';
  const ttlMs = opts.ttlMs ?? 5 * 60 * 1000; // 5 minutes

  const [available, setAvailable] = useState<boolean | null>(null);
  const promptingRef = useRef<Promise<boolean> | null>(null);

  const enabled = useMemo(() => {
    try {
      const v = localStorage.getItem(enabledKey);
      // Default ON if unset
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  }, [enabledKey]);

  const isCachedOk = useCallback(() => {
    try {
      const ts = Number(localStorage.getItem(cacheKey) || '0');
      if (!ts) return false;
      return Date.now() - ts < ttlMs;
    } catch {
      return false;
    }
  }, [cacheKey, ttlMs]);

  const setOkNow = useCallback(() => {
    try {
      localStorage.setItem(cacheKey, String(Date.now()));
    } catch {}
  }, [cacheKey]);

  const ensure = useCallback(async (): Promise<boolean> => {
    if (!isMobileApp()) return true; // Skip on web
    if (!enabled) return true; // Feature disabled
    if (isCachedOk()) return true; // Recently unlocked
    if (!NativeBiometric) return true; // Plugin not present on web build

    if (!promptingRef.current) {
      promptingRef.current = (async () => {
        try {
          const { isAvailable } = await NativeBiometric.isAvailable();
          setAvailable(!!isAvailable);
          if (!isAvailable) return true; // No biometrics, don't block

          await NativeBiometric.verifyIdentity({
            reason: 'Unlock imata',
            title: 'Unlock imata',
            subtitle: 'Confirm it’s you',
            description: 'Use your fingerprint or face to continue',
            maxAttempts: 3,
          });
          setOkNow();
          return true;
        } catch (e: any) {
          // If user cancels or fails, treat as not allowed
          return false;
        } finally {
          promptingRef.current = null;
        }
      })();
    }

    return promptingRef.current;
  }, [enabled, isCachedOk, setOkNow]);

  const setEnabled = useCallback((value: boolean) => {
    try {
      localStorage.setItem(enabledKey, value ? 'true' : 'false');
    } catch {}
  }, [enabledKey]);

  return {
    enabled,
    setEnabled,
    available,
    ensureBiometricGate: ensure,
  } as const;
}
